import React, {
  useCallback,
  useEffect,
  useContext,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DealerSearchContext } from '@rsc/contexts/DealerSearchContext';
import { SiteSettingsContext } from '@rsc/contexts/SiteSettingsContext';
import BlockBuilder from '@rsc/ui-components/sanity/BlockBuilder';
import breakpoints from '../../breakpoints';

const HubContainer = styled.div`
  min-height: 250px;

  @media (min-width: ${breakpoints.sm}) {
    max-width: ${breakpoints.sm};
  }
`;

const FORM_IDS_TO_REDIRECT = [
  '1c58e2db-45e2-481a-a01e-b09c00aa996e', // Prod Form
  '9bd93195-a428-4fd5-a338-206ff5771e5d', // Staging Form
];

const CONTACT_FORMS = [
  '4eb0d2c9-ba68-46a1-8499-5e684e77236e', // Prod Form
  '24ed4601-49a5-4850-8d3e-ae311a2992a6', // Staging Form
];

// If we are using Production HubSpot embed,
// use production scheduler URL, else use lower env.
const SCHEDULE_REDIRECT_URL =
  process.env.GATSBY_HUBSPOT_PORTAL_ID === '5088584'
    ? `https://scheduling.invisiblefence.com/prospect`
    : `https://ifb-scheduling-test.azurewebsites.net/prospect`;

function BlockContentHubspotFormEmbed({ formId, heading, getInternalLink }) {
  const dealerContext = useContext(DealerSearchContext);
  const { siteSettings } = useContext(SiteSettingsContext);
  const initializedForm = useRef();
  const autocomplete = useRef();
  const [formFallbackMode, setFormFallbackMode] = useState(false);

  const updateFormFields = useCallback(
    (address1, city, state, country, postcode) => {
      const addressField = initializedForm.current.querySelector(
        'input[name="address"]'
      );
      const cityField =
        initializedForm.current.querySelector('input[name="city"]');
      const stateField = initializedForm.current.querySelector(
        'input[name="state"]'
      );
      const postalField =
        initializedForm.current.querySelector('input[name="zip"]');
      const countryField = initializedForm.current.querySelector(
        'input[name="country"]'
      );

      if (addressField) addressField.value = address1?.join(' ') || '';
      if (cityField) cityField.value = city || '';
      if (stateField) stateField.value = state?.join(' ') || '';
      if (countryField) countryField.value = country?.join(' ') || '';

      if (postalField) {
        postalField.value = postcode?.join(' ') || '';
        postalField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },
    [initializedForm]
  );

  const updateShelterFields = useCallback(
    (
      shelterName,
      shelterAddress,
      shelterPostcode,
      shelterLocality,
      shelterState
    ) => {
      const shelterField = initializedForm.current.querySelector(
        'input[name="school"]'
      );
      const shelterAddressField = initializedForm.current.querySelector(
        'input[name="shelter_street_address"]'
      );
      const shelterCityField = initializedForm.current.querySelector(
        'input[name="shelter_city"]'
      );
      const shelterStateField = initializedForm.current.querySelector(
        'input[name="shelter_state_region"]'
      );
      const shelterPostalCode = initializedForm.current.querySelector(
        'input[name="shelter_postal_code"]'
      );

      if (shelterAddressField) {
        shelterAddressField.value = shelterAddress || '';
      }

      if (shelterCityField) {
        shelterCityField.value = shelterLocality || '';
      }

      if (shelterStateField) {
        shelterStateField.value = shelterState || '';
      }

      if (shelterPostalCode) {
        shelterPostalCode.value = shelterPostcode || '';
      }

      if (shelterField) {
        shelterField.value = shelterName || '';
      }
    },
    []
  );

  const fillInAddress = useCallback(() => {
    const place = autocomplete.current.getPlace();

    const addressField = initializedForm.current.querySelector(
      'input[name="address"]'
    );

    const shelterField = initializedForm.current.querySelector(
      'input[name="school"]'
    );

    const address1 = [];
    let city = '';
    const state = [];
    const postcode = [];
    const country = [];

    place.address_components.forEach(component => {
      const componentTypes = component.types;

      if (componentTypes.includes('street_number')) {
        address1.push(component.long_name);
      }

      if (componentTypes.includes('route')) {
        address1.push(component.short_name);
      }

      if (componentTypes.includes('subpremise')) {
        address1.push(component.long_name);
      }

      if (
        componentTypes.includes('locality') &&
        componentTypes.includes('political')
      ) {
        city = component.short_name;
      } else if (
        componentTypes.includes('neighborhood') &&
        componentTypes.includes('political') &&
        city === ''
      ) {
        city = component.short_name;
      }

      if (componentTypes.includes('administrative_area_level_1')) {
        state.push(component.short_name);
      }

      if (componentTypes.includes('postal_code')) {
        postcode.push(component.long_name);
      }

      if (componentTypes.includes('country')) {
        country.push(component.short_name);
      }
    });

    let shelterAddress = '';
    let shelterPostcode = '';
    let shelterLocality = '';
    let shelterState = '';

    place.address_components.forEach(component => {
      switch (component.types[0]) {
        case 'street_number':
          shelterAddress = `${component.long_name} ${shelterAddress}`;
          break;
        case 'route':
          shelterAddress += component.short_name;
          break;
        case 'postal_code':
          shelterPostcode = `${component.long_name}${shelterPostcode}`;
          break;
        case 'postal_code_suffix':
          shelterPostcode = `${shelterPostcode}-${component.long_name}`;
          break;
        case 'locality':
          shelterLocality = component.long_name;
          break;
        case 'administrative_area_level_1':
          shelterState = component.short_name;
          break;
        default:
          break;
      }
    });

    if (initializedForm.current) {
      if (addressField) {
        updateFormFields(address1, city, state, country, postcode);
      }
      if (shelterField) {
        updateShelterFields(
          place.name,
          shelterAddress,
          shelterPostcode,
          shelterLocality,
          shelterState
        );
      }
    }
  }, [updateFormFields, updateShelterFields]);

  const googleMapsLoad = useCallback(() => {
    const iFrame = document.querySelector(
      `div[name="${formId}"] iframe[class="hs-form-iframe"]`
    ).contentWindow;
    const googleMapsRef = iFrame.google;

    const addressField = iFrame.document.querySelector('input[name="address"]');
    const shelterField = iFrame.document.querySelector('input[name="school"]');

    const autocompleteField = addressField || shelterField;

    let autocompleteConfig;

    if (addressField) {
      autocompleteConfig = {
        componentRestrictions: { country: ['us', 'ca'] },
        fields: ['address_components', 'name'],
        types: ['address'],
      };
    } else {
      autocompleteConfig = {
        componentRestrictions: {
          country: ['us', 'ca'],
        },
        fields: ['address_components', 'geometry', 'name'],
        types: ['establishment'],
      };
    }

    if (autocompleteField) {
      autocomplete.current = new googleMapsRef.maps.places.Autocomplete(
        autocompleteField,
        autocompleteConfig
      );

      autocomplete.current.addListener('place_changed', fillInAddress);
    }
  }, [fillInAddress, formId]);

  const loadGoogleMaps = useCallback(() => {
    const iFrame = document.querySelector(
      `div[name="${formId}"] iframe[class="hs-form-iframe"]`
    ).contentWindow;
    const formDoc = iFrame.document.body;
    const googleMapsTag = document.createElement('script');
    googleMapsTag.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.GATSBY_GOOGLE_MAPS_KEY}&libraries=places`;
    googleMapsTag.onload = googleMapsLoad;
    formDoc.appendChild(googleMapsTag);
  }, [formId, googleMapsLoad]);

  const handleFormFallbackMode = useCallback(() => {
    if (formFallbackMode) return;

    setFormFallbackMode(true);
  }, [formFallbackMode]);

  useEffect(() => {
    const hubspotTag = document.createElement('script');
    hubspotTag.src = 'https://js.hsforms.net/forms/v2.js';
    hubspotTag.key = 'hubspot';

    window.jQuery =
      window.jQuery ||
      // eslint-disable-next-line func-names
      function (nodeOrSelector) {
        return typeof nodeOrSelector === 'string'
          ? document.querySelector(nodeOrSelector)
          : nodeOrSelector;
      };

    hubspotTag.addEventListener('error', () => {
      handleFormFallbackMode();
    });

    hubspotTag.addEventListener('load', () => {
      let formData = '';
      window?.hbspt?.forms?.create({
        region: 'na1',
        portalId: process.env.GATSBY_HUBSPOT_PORTAL_ID,
        formId,
        version: 'V2_PRERELEASE',
        target: `div[name="${formId}"]`,
        onFormReady($form) {
          initializedForm.current = $form;

          const addressField = $form.querySelector('input[name="address"]');
          const shelterField = $form.querySelector('input[name="school"]');

          if (addressField || shelterField) {
            loadGoogleMaps();
          }

          if (FORM_IDS_TO_REDIRECT.includes(formId)) {
            const firstName = $form.querySelector('input[name="firstname"]');
            const lastName = $form.querySelector('input[name="lastname"]');
            const email = $form.querySelector('input[name="email"]');
            const postalCode = $form.querySelector('input[name="zip"]');
            const referralCodeField = $form.querySelector(
              'input[name="referred_by_code"]'
            );

            const leadFirstName = localStorage.getItem('leadFirstName');
            const leadLastName = localStorage.getItem('leadLastName');
            const leadEmail = localStorage.getItem('leadEmail');
            const leadPostalCode = localStorage.getItem('leadPostalCode');

            const searchParams = new URLSearchParams(window?.location?.search);
            const referralCode =
              searchParams.get('referral_code') ||
              sessionStorage.getItem('referral_code');

            if (leadFirstName) {
              firstName.value = leadFirstName;
            }

            if (leadLastName) {
              lastName.value = leadLastName;
            }

            if (leadEmail) {
              email.value = leadEmail;
            }

            if (leadPostalCode) {
              postalCode.value = leadPostalCode;
            }

            if (referralCode) {
              referralCodeField.value = referralCode;

              // Dispatch event to ensure HubSpot doesn't overwrite the value
              referralCodeField.dispatchEvent(
                new Event('input', { bubbles: true })
              );
            }
          }
        },
        onFormSubmit($form) {
          const searchParams = new URLSearchParams(window?.location?.search);

          if (CONTACT_FORMS.includes(formId)) {
            const dealershipIdField = $form.querySelector(
              'input[name="dealership_id"]'
            );

            const dealerID = searchParams.get('dealerID');
            const storedDealerID = dealerContext?.storedDealer?.id;

            if (dealershipIdField && (dealerID || storedDealerID)) {
              dealershipIdField.value = dealerID || storedDealerID;
            }
          }

          if (FORM_IDS_TO_REDIRECT.includes(formId)) {
            const promo = localStorage.getItem('promo');
            const utmSource = localStorage.getItem('utm_source');
            const utmMedium = localStorage.getItem('utm_medium');
            const utmCampaign = localStorage.getItem('utm_campaign');

            const firstName = $form.querySelector(
              'input[name="firstname"]'
            ).value;
            const lastName = $form.querySelector(
              'input[name="lastname"]'
            ).value;
            const address = $form.querySelector('input[name="address"]').value;
            const city = $form.querySelector('input[name="city"]').value;
            const state = $form.querySelector('input[name="state"]').value;
            const zip = $form.querySelector('input[name="zip"]').value;
            const phone = encodeURIComponent(
              $form.querySelector('input[name="phone"]')?.value || ''
            );
            const email = encodeURIComponent(
              $form.querySelector('input[name="email"]').value
            );

            const referralCode = encodeURIComponent(
              $form.querySelector('input[name="referred_by_code"]')?.value || ''
            );

            formData = `firstName=${firstName}`;
            formData += `&lastName=${lastName}`;
            formData += `&address=${address}`;
            formData += `&city=${city}`;
            formData += `&state=${state}`;
            formData += `&zip=${zip}`;
            formData += `&phoneNumber=${phone}`;
            formData += `&email=${email}`;
            formData += `&referralCode=${referralCode}`;
            formData += `&promo=${promo || ''}`;
            formData += `&source=${utmSource || ''}`;
            formData += `&medium=${utmMedium || ''}`;
            formData += `&campaign=${utmCampaign || ''}`;
            formData += `&path=${window.location.pathname}`;
          }
        },
        onFormSubmitted() {
          if (FORM_IDS_TO_REDIRECT.includes(formId)) {
            window.location = `${SCHEDULE_REDIRECT_URL}/redirect?${formData}`;
          }
        },
      });
    });

    document.body.appendChild(hubspotTag);
  }, [
    dealerContext?.storedDealer?.id,
    formId,
    loadGoogleMaps,
    handleFormFallbackMode,
  ]);

  return (
    <HubContainer>
      {heading && <h2>{heading}</h2>}
      {formId && <div name={formId} />}
      {formFallbackMode && (
        <BlockBuilder
          blocks={siteSettings.manageHubspotFormFallbackMessage.text}
          getInternalLink={getInternalLink}
        />
      )}
    </HubContainer>
  );
}

BlockContentHubspotFormEmbed.propTypes = {
  formId: PropTypes.string.isRequired,
  heading: PropTypes.string,
  getInternalLink: PropTypes.func,
};

BlockContentHubspotFormEmbed.defaultProps = {
  heading: '',
  getInternalLink: () => {},
};

export default BlockContentHubspotFormEmbed;
